import { useCallback, useMemo, useState } from "react";
import { Outlet } from "react-router";
import { UserDict } from "@frontend/lyng/comms/V2";
import "@frontend/lyng/comms/theming.css";
import { useUsersByIdsLazyQuery } from "../../api/generated/graphql";

/**
 * We need this Context/Outlet wrapper to handle state/functionality shared
 * between both the Chat and the Chatlist views in the application.
 * This way we have a single source of truth for users and how to fetch
 * them if needed.
 */

export type ChatWrapperContext = {
  userDict: UserDict;
  fetchUsers: (ids: string[]) => void;
  fetchingUsers: boolean;
};

export const ChatWrapper = () => {
  const [userDict, setUserDict] = useState<UserDict>({});

  const [fetchUsersLazy, { loading: fetchingUsers }] = useUsersByIdsLazyQuery({
    fetchPolicy: "cache-first",
  });

  const fetchUsers = useCallback(
    (ids: string[]) => {
      fetchUsersLazy({
        variables: { ids },
      }).then(({ data }) => {
        const newUsers = (data?.usersByIds ?? []).reduce<UserDict>(
          (accumulated, { id, firstName, lastName }) => {
            return { ...accumulated, [id]: { firstName, lastName } };
          },
          {},
        );
        setUserDict((current) => ({ ...current, ...newUsers }));
      });
    },
    [fetchUsersLazy],
  );

  const context: ChatWrapperContext = useMemo(
    () => ({ userDict, fetchUsers, fetchingUsers }),
    [userDict, fetchUsers, fetchingUsers],
  );
  return <Outlet context={context} />;
};
